import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Plus, Check, X, AlertCircle, Loader2, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../pages/ProjectsList';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800' },
};

export function ChangeOrders({ projectId, canEdit, onTotalsChange }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ description: '', amount: '' });
  const [saving, setSaving] = useState(false);
  const [approvedTotal, setApprovedTotal] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(0);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await projectsAPI.listChangeOrders(projectId);
      setOrders(res.data.change_orders || []);
      setApprovedTotal(res.data.approved_total || 0);
      setPendingTotal(res.data.pending_total || 0);
      if (onTotalsChange) onTotalsChange(res.data.approved_total || 0, res.data.pending_total || 0);
    } catch {
      toast.error('Failed to load change orders');
    } finally {
      setLoading(false);
    }
  }, [projectId, onTotalsChange]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleAdd = async () => {
    if (!form.description.trim() || !form.amount) return;
    setSaving(true);
    try {
      await projectsAPI.createChangeOrder(projectId, {
        description: form.description.trim(),
        amount: parseFloat(form.amount),
      });
      setForm({ description: '', amount: '' });
      setAdding(false);
      toast.success('Change order added');
      fetchOrders();
    } catch {
      toast.error('Failed to add change order');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (coId, newStatus) => {
    try {
      await projectsAPI.updateChangeOrder(projectId, coId, { status: newStatus });
      toast.success(`Change order ${newStatus}`);
      fetchOrders();
    } catch {
      toast.error('Failed to update change order');
    }
  };

  const handleDelete = async (coId) => {
    try {
      await projectsAPI.deleteChangeOrder(projectId, coId);
      toast.success('Change order deleted');
      fetchOrders();
    } catch {
      toast.error('Failed to delete change order');
    }
  };

  if (loading) return null;

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="change-orders-section">
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 bg-muted/20 cursor-pointer select-none"
        onClick={() => setExpanded(p => !p)}
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <span className="text-sm font-semibold">Change Orders</span>
          <Badge variant="secondary" className="text-[10px]">{orders.length}</Badge>
          {approvedTotal !== 0 && (
            <span className={`text-xs font-semibold tabular-nums ${approvedTotal > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {approvedTotal > 0 ? '+' : ''}{formatCurrency(approvedTotal)} approved
            </span>
          )}
          {pendingTotal !== 0 && (
            <span className="text-xs text-amber-600 tabular-nums">
              ({pendingTotal > 0 ? '+' : ''}{formatCurrency(pendingTotal)} pending)
            </span>
          )}
        </div>
        {canEdit && expanded && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={e => { e.stopPropagation(); setAdding(true); }} data-testid="add-change-order-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add CO
          </Button>
        )}
      </div>

      {expanded && (
        <div className="divide-y">
          {/* Add form */}
          {adding && (
            <div className="flex gap-2 items-start p-3 bg-primary/5" data-testid="change-order-form" onClick={e => e.stopPropagation()}>
              <Input
                autoFocus
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Description (e.g., Added electrical panel upgrade)"
                className="flex-1 h-8 text-sm"
                onKeyDown={e => { if (e.key === 'Enter' && form.description && form.amount) handleAdd(); if (e.key === 'Escape') { setAdding(false); setForm({ description: '', amount: '' }); } }}
                data-testid="co-description-input"
              />
              <Input
                type="number"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="Amount"
                className="w-[120px] h-8 text-sm text-right"
                onKeyDown={e => { if (e.key === 'Enter' && form.description && form.amount) handleAdd(); }}
                data-testid="co-amount-input"
              />
              <Button size="sm" className="h-8" onClick={handleAdd} disabled={saving || !form.description.trim() || !form.amount} data-testid="co-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
              </Button>
              <Button size="sm" variant="ghost" className="h-8" onClick={() => { setAdding(false); setForm({ description: '', amount: '' }); }}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}

          {/* Empty state */}
          {orders.length === 0 && !adding && (
            <div className="p-4 text-center text-sm text-muted-foreground" data-testid="no-change-orders">
              No change orders yet
            </div>
          )}

          {/* Order list */}
          {orders.map(co => {
            const cfg = STATUS_CONFIG[co.status] || STATUS_CONFIG.pending;
            return (
              <div key={co.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`change-order-${co.id}`}>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${co.status === 'rejected' ? 'line-through text-muted-foreground' : ''}`}>
                    {co.description}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {co.creator_name && `by ${co.creator_name}`}
                    {co.created_at && ` · ${new Date(co.created_at).toLocaleDateString()}`}
                    {co.status !== 'pending' && co.approver_name && ` · ${co.status} by ${co.approver_name}`}
                  </p>
                </div>
                <span className={`tabular-nums font-semibold ${co.amount >= 0 ? '' : 'text-red-600'}`}>
                  {co.amount >= 0 ? '+' : ''}{formatCurrency(co.amount)}
                </span>
                <Badge variant="outline" className={`text-[10px] ${cfg.color}`} data-testid={`co-status-${co.id}`}>
                  {cfg.label}
                </Badge>
                {canEdit && co.status === 'pending' && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => handleStatusChange(co.id, 'approved')} title="Approve" data-testid={`co-approve-${co.id}`}>
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleStatusChange(co.id, 'rejected')} title="Reject" data-testid={`co-reject-${co.id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
                {canEdit && co.status !== 'pending' && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={() => handleDelete(co.id)} title="Delete" data-testid={`co-delete-${co.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
